import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, CircularProgress, Alert } from '@mui/material';
import Box from '@mui/material/Box';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

/**
 * AdviserSelection component for selecting an adviser from a list.
 * @param {Object} props
 * @param {Array} props.advisers - List of adviser objects
 * @param {string} props.selectedAdviser - Currently selected adviser ID
 * @param {boolean} props.advisersLoading - Loading state
 * @param {string} [props.advisersError] - Error message
 * @param {Function} props.onAdviserChange - Callback when adviser changes
 */
export const AdviserSelection = ({ 
  advisers, 
  selectedAdviser, 
  advisersLoading, 
  advisersError, 
  onAdviserChange 
}) => {
  // Sort advisers by full name
  const sortedAdvisers = useMemo(() => {
    return [...advisers].sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [advisers]);

  if (advisersError) {
    return (
      <Alert severity="warning" sx={{ mb: 4 }}>
        <MDTypography variant="body2">
          {advisersError}
        </MDTypography>
      </Alert>
    );
  }

  return (
    <MDBox mb={4} display="flex" justifyContent="center">
      <Autocomplete
        options={sortedAdvisers}
        getOptionLabel={(option) => option.full_name || ''}
        value={sortedAdvisers.find(adviser => adviser.id === selectedAdviser) || null}
        onChange={(event, newValue) => {
          onAdviserChange(newValue ? newValue.id : null);
        }}
        loading={advisersLoading}
        disabled={advisersLoading}
        filterOptions={(options, { inputValue }) => {
          const searchTerm = inputValue.toLowerCase().trim();
          if (!searchTerm) return options;
          
          return options.filter(adviser => {
            // Search in full name
            if (adviser.full_name && adviser.full_name.toLowerCase().includes(searchTerm)) {
              return true;
            }
            
            // Search in email
            if (adviser.email && adviser.email.toLowerCase().includes(searchTerm)) {
              return true;
            }
            
            // Search in job title
            if (adviser.job_title && adviser.job_title.toLowerCase().includes(searchTerm)) {
              return true;
            }
            
            // Search in first name or last name separately
            const nameParts = adviser.full_name ? adviser.full_name.toLowerCase().split(' ') : [];
            if (nameParts.some(part => part.includes(searchTerm))) {
              return true;
            }
            
            return false;
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Adviser"
            placeholder="Search by name, email, or job title..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {advisersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              minWidth: 400,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'info.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'info.main',
                },
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ py: 2, px: 3 }}>
            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%' }}>
              {/* Profile Picture */}
              <MDBox
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  backgroundColor: 'grey.300',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                {option.profile_pic_path ? (
                  <img
                    src={option.profile_pic_path}
                    alt={`${option.full_name} profile`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <MDBox
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: option.profile_pic_path ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'info.main',
                    color: '#ffffff',
                    fontSize: '1.2rem',
                    fontWeight: 600,
                  }}
                >
                  {option.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </MDBox>
              </MDBox>

              {/* Adviser Info */}
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <MDTypography 
                  variant="body1" 
                  fontWeight="bold"
                  sx={{ 
                    color: option.is_current_user ? 'info.main' : 'text.primary',
                    fontSize: '1rem',
                    mb: 1,
                  }}
                >
                  {option.full_name}
                  {option.is_current_user && (
                    <MDTypography 
                      component="span" 
                      variant="caption" 
                      color="info.main"
                      sx={{ ml: 1, fontWeight: 600 }}
                    >
                      (You)
                    </MDTypography>
                  )}
                </MDTypography>
                <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  {option.email && (
                    <MDTypography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.875rem',
                        fontWeight: 400,
                      }}
                    >
                      {option.email}
                    </MDTypography>
                  )}
                  {option.job_title && (
                    <MDTypography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.875rem',
                        fontWeight: 400,
                      }}
                    >
                      {option.job_title}
                    </MDTypography>
                  )}
                  {option.job_title && option.branch_name && (
                    <MDTypography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.875rem',
                        fontWeight: 400,
                      }}
                    >
                      •
                    </MDTypography>
                  )}
                  {option.branch_name && (
                    <MDTypography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.875rem',
                        fontWeight: 400,
                      }}
                    >
                      {option.branch_name}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          </Box>
        )}
        renderTags={() => null}
        noOptionsText="No advisers found"
        loadingText="Loading advisers..."
        sx={{
          minWidth: 400,
        }}
      />
    </MDBox>
  );
};

AdviserSelection.propTypes = {
  advisers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    full_name: PropTypes.string.isRequired,
    profile_pic_path: PropTypes.string,
    email: PropTypes.string,
    job_title: PropTypes.string,
    branch_name: PropTypes.string, // now required from backend
    is_current_user: PropTypes.bool,
  })).isRequired,
  selectedAdviser: PropTypes.string,
  advisersLoading: PropTypes.bool.isRequired,
  advisersError: PropTypes.string,
  onAdviserChange: PropTypes.func.isRequired,
}; 